import { useState, useEffect, useRef } from 'react'
import { X, Camera, Upload } from 'lucide-react'
import type { Agent } from '../../services/agentService'
import { agentService } from '../../services/agentService'
import { supabase } from '../../lib/supabase'
import { ImageLibraryModal } from './ImageLibraryModal'
import { ImageLibraryButton } from './ImageLibraryButton'

interface EditAgentProfileModalProps {
    isOpen: boolean
    onClose: () => void
    agent: Agent
    onSave: (updatedAgent: Partial<Agent>) => Promise<void>
}

export function EditAgentProfileModal({ isOpen, onClose, agent, onSave }: EditAgentProfileModalProps) {
    const [name, setName] = useState(agent.name)
    const [description, setDescription] = useState(agent.description || '')
    const [functionDescription, setFunctionDescription] = useState(agent.function_description || '')
    const [avatarFile, setAvatarFile] = useState<File | null>(null)
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
    const [bannerFile, setBannerFile] = useState<File | null>(null)
    const [bannerPreview, setBannerPreview] = useState<string | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isLibraryOpen, setIsLibraryOpen] = useState(false)
    const [avatarSource, setAvatarSource] = useState<'upload' | 'library' | null>(null)
    const avatarInputRef = useRef<HTMLInputElement>(null)
    const bannerInputRef = useRef<HTMLInputElement>(null)

    // Reset form when agent changes
    useEffect(() => {
        setName(agent.name)
        setDescription(agent.description || '')
        setFunctionDescription(agent.function_description || '')
        setAvatarFile(null)
        setAvatarPreview(null)
        setBannerFile(null)
        setBannerPreview(null)
        setAvatarSource(null)
    }, [agent])

    // Cleanup preview URLs
    useEffect(() => {
        return () => {
            if (avatarPreview) {
                URL.revokeObjectURL(avatarPreview)
            }
            if (bannerPreview) {
                URL.revokeObjectURL(bannerPreview)
            }
        }
    }, [avatarPreview, bannerPreview])

    if (!isOpen) return null

    const handleAvatarClick = () => {
        avatarInputRef.current?.click()
    }

    const handleBannerClick = () => {
        bannerInputRef.current?.click()
    }

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Por favor, selecione uma imagem válida')
            return
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('A imagem deve ter no máximo 5MB')
            return
        }

        setAvatarFile(file)
        setAvatarSource('upload')

        // Create preview
        const previewUrl = URL.createObjectURL(file)
        setAvatarPreview(previewUrl)
    }

    const handleLibraryImageSelect = (imageUrl: string) => {
        setAvatarPreview(imageUrl)
        setAvatarFile(null)
        setAvatarSource('library')
        setIsLibraryOpen(false)
    }

    const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Por favor, selecione uma imagem válida')
            return
        }

        // Validate file size (max 10MB for banner)
        if (file.size > 10 * 1024 * 1024) {
            alert('A imagem do banner deve ter no máximo 10MB')
            return
        }

        setBannerFile(file)

        // Create preview
        const previewUrl = URL.createObjectURL(file)
        setBannerPreview(previewUrl)
    }

    const handleSave = async () => {
        // Validate name
        if (!name || name.trim().length < 2) {
            alert('O nome deve ter pelo menos 2 caracteres')
            return
        }

        setIsSaving(true)
        setIsUploading(true)
        try {
            let avatarUrl = agent.avatar_url
            let bannerUrl = agent.banner_url

            // Upload avatar if changed
            if (avatarFile) {
                try {
                    const { data: { user } } = await supabase.auth.getUser()
                    if (user) {
                        avatarUrl = await agentService.uploadAvatar(user.id, avatarFile)
                    }
                } catch (uploadError) {
                    console.error('Error uploading avatar:', uploadError)
                    alert('Erro ao fazer upload da foto. As outras alterações serão salvas.')
                }
            } else if (avatarSource === 'library' && avatarPreview) {
                // Copy library image to user's storage
                try {
                    const { data: { user } } = await supabase.auth.getUser()
                    if (user) {
                        avatarUrl = await agentService.copyLibraryImage(user.id, avatarPreview)
                    }
                } catch (uploadError) {
                    console.error('Error copying library image:', uploadError)
                    alert('Erro ao copiar imagem da biblioteca. As outras alterações serão salvas.')
                }
            }

            // Upload banner if changed
            if (bannerFile) {
                try {
                    console.log('Starting banner upload...', { bannerFile })
                    const { data: { user } } = await supabase.auth.getUser()
                    if (user) {
                        bannerUrl = await agentService.uploadBanner(user.id, bannerFile)
                        console.log('Banner uploaded successfully:', bannerUrl)
                    }
                } catch (uploadError) {
                    console.error('Error uploading banner:', uploadError)
                    alert('Erro ao fazer upload do banner. As outras alterações serão salvas.')
                }
            }

            console.log('Saving profile with:', {
                name: name.trim(),
                description: description.trim() || null,
                function_description: functionDescription.trim() || null,
                avatar_url: avatarUrl,
                banner_url: bannerUrl
            })

            await onSave({
                name: name.trim(),
                description: description.trim() || null,
                function_description: functionDescription.trim() || null,
                avatar_url: avatarUrl,
                banner_url: bannerUrl
            })
            console.log('Profile saved successfully')
            onClose()
        } catch (error) {
            console.error('Error saving agent profile:', error)
            alert('Erro ao salvar perfil. Tente novamente.')
        } finally {
            setIsSaving(false)
            setIsUploading(false)
        }
    }

    const handleClose = () => {
        // Check if there are unsaved changes
        const hasChanges =
            name !== agent.name ||
            description !== (agent.description || '') ||
            functionDescription !== (agent.function_description || '') ||
            avatarFile !== null ||
            bannerFile !== null

        if (hasChanges) {
            if (confirm('Você tem alterações não salvas. Deseja descartar?')) {
                onClose()
            }
        } else {
            onClose()
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="relative w-full max-w-[600px] max-h-[90vh] bg-white dark:bg-black rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-900 rounded-full transition-colors"
                        disabled={isSaving}
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                        Editar perfil
                    </h2>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-1.5 bg-black dark:bg-white text-white dark:text-black rounded-full font-bold text-[15px] hover:bg-gray-900 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isUploading ? 'Enviando...' : isSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Banner Upload */}
                    <div className="relative h-[200px] bg-gradient-to-br from-yellow-200 via-pink-200 to-blue-200 rounded-xl overflow-hidden">
                        {bannerPreview || agent.banner_url ? (
                            <img
                                src={bannerPreview || agent.banner_url || ''}
                                alt="Banner"
                                className="w-full h-full object-cover"
                            />
                        ) : null}
                        {/* Camera overlay */}
                        <button
                            onClick={handleBannerClick}
                            disabled={isUploading}
                            className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors cursor-pointer disabled:cursor-not-allowed"
                        >
                            <Camera className="w-10 h-10 text-white" />
                        </button>
                        {/* Hidden file input */}
                        <input
                            ref={bannerInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleBannerChange}
                            className="hidden"
                        />
                    </div>

                    {/* Avatar Upload */}
                    <div className="flex flex-col items-center gap-4">
                        <div className="relative">
                            <div className="w-[133px] h-[133px] rounded-full p-[4px] bg-gradient-to-br from-blue-400 via-purple-500 to-pink-400">
                                <div className="w-full h-full rounded-full overflow-hidden bg-white dark:bg-black relative">
                                    {avatarPreview || agent.avatar_url ? (
                                        <img
                                            src={avatarPreview || agent.avatar_url || ''}
                                            alt={agent.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-800">
                                            <span className="text-2xl font-bold text-gray-500">
                                                {agent.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    {/* Camera overlay */}
                                    <button
                                        onClick={handleAvatarClick}
                                        disabled={isUploading}
                                        className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors cursor-pointer disabled:cursor-not-allowed"
                                    >
                                        <Camera className="w-8 h-8 text-white" />
                                    </button>
                                </div>
                            </div>
                            {/* Hidden file input */}
                            <input
                                ref={avatarInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleAvatarChange}
                                className="hidden"
                            />
                        </div>

                        {/* Upload Options */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleAvatarClick}
                                disabled={isUploading}
                                className="
                                    flex items-center justify-center gap-2
                                    px-4 py-2 rounded-lg
                                    bg-gray-100 dark:bg-gray-800
                                    text-gray-700 dark:text-gray-300
                                    text-sm font-medium
                                    hover:bg-gray-200 dark:hover:bg-gray-700
                                    transition-colors duration-200
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                                "
                            >
                                <Upload className="w-4 h-4" />
                                <span>Upload</span>
                            </button>
                            <ImageLibraryButton
                                onClick={() => setIsLibraryOpen(true)}
                                disabled={isUploading}
                            />
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4 mt-4">
                        {/* Name Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Nome
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={50}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Nome do agente"
                            />
                            <div className="flex justify-between mt-1">
                                <span className="text-xs text-gray-500">Obrigatório</span>
                                <span className="text-xs text-gray-500">{name.length}/50</span>
                            </div>
                        </div>

                        {/* Bio/Description Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Bio / Sobre
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={160}
                                rows={3}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                placeholder="Descreva o agente..."
                            />
                            <div className="flex justify-end mt-1">
                                <span className="text-xs text-gray-500">{description.length}/160</span>
                            </div>
                        </div>

                        {/* Function Description Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Função / Cargo
                            </label>
                            <textarea
                                value={functionDescription}
                                onChange={(e) => setFunctionDescription(e.target.value)}
                                maxLength={100}
                                rows={2}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                placeholder="Ex: CEO, Desenvolvedor, Analista..."
                            />
                            <div className="flex justify-end mt-1">
                                <span className="text-xs text-gray-500">{functionDescription.length}/100</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Library Modal */}
            <ImageLibraryModal
                isOpen={isLibraryOpen}
                onClose={() => setIsLibraryOpen(false)}
                onSelectImage={handleLibraryImageSelect}
                currentImage={avatarPreview}
            />
        </div>
    )
}
